import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Button, IconButton } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppLayout from "../../components/AppLayout";
import { UnidadeDistribuidorService } from "../../service/unidade_distribuidor_service";
import { useUser } from "../../context/UserContext";
import { ehGestaoDeFornecimento } from "../../utils/licenca";

// Importar os componentes das abas
import AbaEmpresa from "./abas/AbaEmpresa";
import AbaProdutos from "./abas/AbaProdutos";
import AbaProcessos from "./abas/AbaProcessos";
import AbaPlataformaRastreio from "./abas/AbaPlataformaRastreio";

function DistribuidoraCad() {
	const { id } = useParams();
	const navigate = useNavigate();
	const { user, refreshUser } = useUser();
	const [abaAtiva, setAbaAtiva] = useState("empresa");
	// Processos e plataforma de rastreio são do beneficiamento e das etiquetas:
	// fora da licença de gestão de fornecimento. O backend ignora os dois no
	// corpo do cadastro, então o que já estiver salvo continua intacto.
	const semBeneficiamento = ehGestaoDeFornecimento(user);
	const [loading, setLoading] = useState(false);
	const [salvando, setSalvando] = useState(false);

	// Estados para cada aba
	const [dadosEmpresa, setDadosEmpresa] = useState({});
	const [produtos, setProdutos] = useState([]);
	const [processos, setProcessos] = useState([]);
	const [dadosPlataforma, setDadosPlataforma] = useState({});

	// Referência para função de adicionar documento da AbaEmpresa
	const [addDocumentoHandler, setAddDocumentoHandler] = useState(null);
	// Referência para função de adicionar produto da AbaProdutos
	const [addProdutoHandler, setAddProdutoHandler] = useState(null);
	// Referência para função de adicionar processo da AbaProcessos
	const [addProcessoHandler, setAddProcessoHandler] = useState(null);

	const formatCpfCnpj = useCallback((value) => {
		if (!value) return "";
		const cleanValue = value.replace(/\D/g, "");

		if (cleanValue.length <= 11) {
			// Formato CPF: 000.000.000-00
			return cleanValue
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d{1,2})$/, "$1-$2");
		} else {
			// Formato CNPJ: 00.000.000/0000-00
			return cleanValue
				.replace(/(\d{2})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1.$2")
				.replace(/(\d{3})(\d)/, "$1/$2")
				.replace(/(\d{4})(\d{1,2})$/, "$1-$2");
		}
	}, []);

	const loadDistribuidora = useCallback(async (distribuidoraId) => {
		setLoading(true);
		try {
			const unidadeService = new UnidadeDistribuidorService();
			const data = await unidadeService.getById(distribuidoraId);

			// Carregar dados da empresa
			setDadosEmpresa({
				id: data.id,
				nome: data.nome || "",
				nomeFantasia: data.nomeFantasia || "",
				cpfCnpj: formatCpfCnpj(data.cpf_cnpj || ""),
				ieRg: data.ieRg || "",
				email: data.email || "",
				telefone: data.telefone || "",
				cep: data.endereco?.cep || "",
				uf: data.endereco?.uf || "",
				cidade: data.endereco?.cidade || "",
				bairro: data.endereco?.bairro || "",
				logradouro: data.endereco?.logradouro || "",
				latitude: data.endereco?.latitude || "",
				longitude: data.endereco?.longitude || "",
				documentos: data.documentos || [],
			});

			// Carregar produtos
			const produtosFormatados = (data.produtos || []).map((produto) => ({
				id: produto.id,
				tipo: produto.tipo,
				cultivo: produto.cultivo,
				codigoBarras: produto.codigoBarras || "",
				embalagens: (produto.embalagens || []).map((emb) => ({
					id: emb.id,
					descricao: emb.descricao,
					quantidade: emb.quantidade,
					unidadeMedida: emb.unidadeMedida,
				})),
			}));
			setProdutos(produtosFormatados);

			// Carregar processos
			const processosFormatados = (data.processos || []).map((processo) => ({
				id: processo.id,
				nome: processo.nome,
				etapas: (processo.etapas || []).map((etapa) => ({
					id: etapa.id,
					nome: etapa.nome,
					posicao: etapa.posicao,
					solicitaProduto: Boolean(etapa.solicitaProduto),
				})),
			}));
			setProcessos(processosFormatados);

			// Carregar plataforma
			if (data.plataformaRastreio) {
				setDadosPlataforma({
					nomeDistribuidora: data.plataformaRastreio.nomeDistribuidora || "",
					descricaoEmpresa: data.plataformaRastreio.descricaoEmpresa || "",
					foto: data.plataformaRastreio.fotoUrl || null,
					galeria: data.plataformaRastreio.galeria || [],
					whatsapp: data.plataformaRastreio.whatsapp || "",
					instagram: data.plataformaRastreio.instagram || "",
					facebook: data.plataformaRastreio.facebook || "",
					website: data.plataformaRastreio.website || "",
				});
			}
		} catch (error) {
			console.error("Erro ao carregar distribuidora:", error);
			alert(error.message || "Erro ao carregar dados da distribuidora");
		} finally {
			setLoading(false);
		}
	}, [formatCpfCnpj]);

	useEffect(() => {
		if (id) {
			loadDistribuidora(id);
		}
	}, [id, loadDistribuidora]);

	const ORDEM_ABAS = semBeneficiamento
		? ["empresa", "produtos"]
		: ["empresa", "produtos", "processos", "plataforma"];

	// Qual é a última aba depende da licença: na de gestão de fornecimento o
	// fluxo termina em Produtos. Fixar "plataforma" aqui deixava o botão preso
	// em "Próximo" numa aba que já era a última, e o cadastro nunca era salvo.
	const naUltimaAba = abaAtiva === ORDEM_ABAS[ORDEM_ABAS.length - 1];

	const handleProximaAba = () => {
		if (abaAtiva === "empresa" && (!dadosEmpresa.nome || !dadosEmpresa.cpfCnpj)) {
			alert("Preencha os campos obrigatórios: Nome e CPF/CNPJ");
			return;
		}
		const indice = ORDEM_ABAS.indexOf(abaAtiva);
		if (indice < ORDEM_ABAS.length - 1) {
			setAbaAtiva(ORDEM_ABAS[indice + 1]);
		}
	};

	const handleSalvar = async () => {
		try {
			// Validações básicas
			if (!dadosEmpresa.nome || !dadosEmpresa.cpfCnpj) {
				alert("Preencha os campos obrigatórios: Nome e CPF/CNPJ");
				setAbaAtiva("empresa");
				return;
			}

			setSalvando(true);

			// Criar FormData para enviar arquivos
			const formData = new FormData();

			// Obter empresaDistribuidorId do usuário logado (UserContext)
			const empresaDistribuidorId = user?.empresaDistribuidorId || localStorage.getItem("selected_empresa_id");

			// Preparar dados para envio
			const unidadeData = {
				id: dadosEmpresa.id || undefined,
				nome: dadosEmpresa.nome,
				nomeFantasia: dadosEmpresa.nomeFantasia,
				cpf_cnpj: dadosEmpresa.cpfCnpj ? dadosEmpresa.cpfCnpj.replace(/\D/g, "") : "",
				ieRg: dadosEmpresa.ieRg,
				email: dadosEmpresa.email,
				telefone: dadosEmpresa.telefone,
				empresaDistribuidorId: empresaDistribuidorId, // Adicionar empresaDistribuidorId
				endereco: {
					cep: dadosEmpresa.cep,
					uf: dadosEmpresa.uf,
					cidade: dadosEmpresa.cidade,
					bairro: dadosEmpresa.bairro,
					logradouro: dadosEmpresa.logradouro,
					latitude: dadosEmpresa.latitude ? parseFloat(dadosEmpresa.latitude) : undefined,
					longitude: dadosEmpresa.longitude ? parseFloat(dadosEmpresa.longitude) : undefined,
				},
				documentos: [],
				produtos: [],
				processos: [],
				plataforma: undefined,
			};

			// Processar documentos
			if (dadosEmpresa.documentos && dadosEmpresa.documentos.length > 0) {
				dadosEmpresa.documentos.forEach((doc, index) => {
					unidadeData.documentos.push({
						nome: doc.nome,
						dataExpedicao: doc.dataExpedicao,
						dataValidade: doc.dataValidade,
					});

					// Adicionar arquivo se for um File
					if (doc.arquivo && doc.arquivo instanceof File) {
						formData.append(`documento_${index}`, doc.arquivo);
					}
				});
			}

			// Processar produtos
			if (produtos && produtos.length > 0) {
				unidadeData.produtos = produtos.map((produto) => ({
					id: produto.id || undefined,
					tipo: produto.tipo,
					cultivoId: produto.cultivo?.id,
					codigoBarras: produto.codigoBarras || undefined,
					embalagens: (produto.embalagens || []).map((emb) => ({
						descricao: emb.descricao,
						quantidade: parseFloat(emb.quantidade),
						unidadeMedidaId: emb.unidadeMedida?.id,
					})),
				}));
			}

			// Processar processos
			if (processos && processos.length > 0) {
				unidadeData.processos = processos.map((processo) => ({
					id: processo.id || undefined,
					nome: processo.nome,
					etapas: (processo.etapas || []).map((etapa) => ({
						nome: etapa.nome,
						posicao: etapa.posicao,
						solicitaProduto: Boolean(etapa.solicitaProduto),
					})),
				}));
			}

			// Processar plataforma de rastreio
			unidadeData.plataforma = {
				nomeDistribuidora: dadosPlataforma.nomeDistribuidora || undefined,
				descricaoEmpresa: dadosPlataforma.descricaoEmpresa || undefined,
				// Foto existente (URL string) - não é um File novo
				fotoUrl: dadosPlataforma.foto && typeof dadosPlataforma.foto === "string"
					? dadosPlataforma.foto
					: undefined,
				// URLs existentes da galeria (strings), excluindo Files novos
				galeria: (dadosPlataforma.galeria || []).filter((img) => typeof img === "string"),
				whatsapp: dadosPlataforma.whatsapp || undefined,
				instagram: dadosPlataforma.instagram || undefined,
				facebook: dadosPlataforma.facebook || undefined,
				website: dadosPlataforma.website || undefined,
			};

			// Foto nova (File object)
			if (dadosPlataforma.foto && dadosPlataforma.foto instanceof File) {
				formData.append("plataforma_foto", dadosPlataforma.foto);
			}

			// Galeria - apenas os novos Files, indexados separadamente
			let galeriaFileIndex = 0;
			(dadosPlataforma.galeria || []).forEach((img) => {
				if (img instanceof File) {
					formData.append(`galeria_file_${galeriaFileIndex}`, img);
					galeriaFileIndex++;
				}
			});

			// Adicionar dados JSON ao FormData
			formData.append("data", JSON.stringify(unidadeData));

			// Salvar no backend
			const unidadeService = new UnidadeDistribuidorService();

			if (id) {
				// Atualizar
				await unidadeService.update(formData);
				alert("Unidade distribuidora atualizada com sucesso!");
				navigate("/distribuidora");
			} else {
				// Criar
				await unidadeService.save(formData);
				alert("Unidade distribuidora cadastrada com sucesso!");

				// Atualizar o perfil no UserContext para obter as unidades atualizadas
				const updatedProfile = await refreshUser();

				// Verificar se é a primeira unidade do usuário
				const unidades = updatedProfile?.unidadesDistribuidoras || [];

				if (unidades.length === 1) {
					// É a primeira unidade, seleciona automaticamente
					const primeiraUnidade = unidades[0].unidadeDistribuidora;
					localStorage.setItem("selected_unidade_id", primeiraUnidade.id.toString());
					if (primeiraUnidade.empresaDistribuidorId) {
						localStorage.setItem("selected_empresa_id", primeiraUnidade.empresaDistribuidorId.toString());
					}
					navigate("/home");
				} else {
					// Não é a primeira, redireciona para listagem
					navigate("/distribuidora");
				}
			}
		} catch (error) {
			console.error("Erro ao salvar unidade distribuidora:", error);
			alert(error.message || "Erro ao salvar unidade distribuidora");
		} finally {
			setSalvando(false);
		}
	};

	const handleAddDocumentos = () => {
		// Se está na aba empresa e existe o handler, executa
		if (abaAtiva === "empresa" && addDocumentoHandler) {
			addDocumentoHandler();
		}
	};

	const handleAddProdutos = () => {
		// Se está na aba produtos e existe o handler, executa
		if (abaAtiva === "produtos" && addProdutoHandler) {
			addProdutoHandler();
		}
	};

	const handleAddProcessos = () => {
		// Se está na aba processos e existe o handler, executa
		if (abaAtiva === "processos" && addProcessoHandler) {
			addProcessoHandler();
		}
	};

	const renderAbaContent = () => {
		switch (abaAtiva) {
			case "empresa":
				return (
					<AbaEmpresa
						dados={dadosEmpresa}
						setDados={setDadosEmpresa}
						onAddDocumentoClick={setAddDocumentoHandler}
					/>
				);
			case "produtos":
				return (
					<AbaProdutos
						produtos={produtos}
						setProdutos={setProdutos}
						onAddProdutoClick={setAddProdutoHandler}
					/>
				);
			case "processos":
				return (
					<AbaProcessos
						processos={processos}
						setProcessos={setProcessos}
						onAddProcessoClick={setAddProcessoHandler}
					/>
				);
			case "plataforma":
				return (
					<AbaPlataformaRastreio
						dados={dadosPlataforma}
						setDados={setDadosPlataforma}
					/>
				);
			default:
				return null;
		}
	};

	if (loading) {
		return (
			<Box
				sx={{
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					minHeight: "100vh",
				}}
			>
				<Typography>Carregando...</Typography>
			</Box>
		);
	}

	return (
		<AppLayout currentPage="distribuidora">
			<Box sx={{ maxWidth: 1400, mx: "auto" }}>
					{/* Header com título */}
					<Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 1 }}>
						<IconButton onClick={() => navigate("/distribuidora")} sx={{ color: "#666" }}>
							<ArrowBackIcon />
						</IconButton>
						<Typography
							variant="h5"
							sx={{
								color: "#666",
								fontWeight: 500,
								fontSize: "20px",
							}}
						>
							Cadastro de Unidade de Distribuição
						</Typography>
					</Box>

					{/* Container com fundo branco */}
					<Box
						sx={{
							bgcolor: "white",
							borderRadius: "4px",
							boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
							overflow: "hidden",
						}}
					>
						{/* Header com abas e botão */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								px: 6,
								pt: 4,
								pb: 0,
							}}
						>
							{/* Navegação das abas */}
							<Box sx={{ display: "flex", gap: 0.5 }}>
								<Button
									onClick={() => setAbaAtiva("empresa")}
									sx={{
										textTransform: "none",
										fontSize: "15px",
										fontWeight: 500,
										bgcolor: abaAtiva === "empresa" ? "#1a9e8f" : "transparent",
										color: abaAtiva === "empresa" ? "white" : "#1a9e8f",
										borderRadius: "6px 6px 0 0",
										px: 3,
										py: 1.5,
										minWidth: "auto",
										minHeight: "42px",
										"&:hover": {
											bgcolor: abaAtiva === "empresa" ? "#138a7d" : "rgba(23, 162, 184, 0.08)",
										},
									}}
								>
									Empresa
								</Button>
								<Button
									onClick={() => setAbaAtiva("produtos")}
									sx={{
										textTransform: "none",
										fontSize: "15px",
										fontWeight: 500,
										bgcolor: abaAtiva === "produtos" ? "#1a9e8f" : "transparent",
										color: abaAtiva === "produtos" ? "white" : "#1a9e8f",
										borderRadius: "6px 6px 0 0",
										px: 3,
										py: 1.5,
										minWidth: "auto",
										minHeight: "42px",
										"&:hover": {
											bgcolor: abaAtiva === "produtos" ? "#138a7d" : "rgba(23, 162, 184, 0.08)",
										},
									}}
								>
									Produtos
								</Button>
								{!semBeneficiamento && (
									<>
										<Button
											onClick={() => setAbaAtiva("processos")}
											sx={{
												textTransform: "none",
												fontSize: "15px",
												fontWeight: 500,
												bgcolor: abaAtiva === "processos" ? "#1a9e8f" : "transparent",
												color: abaAtiva === "processos" ? "white" : "#1a9e8f",
												borderRadius: "6px 6px 0 0",
												px: 3,
												py: 1.5,
												minWidth: "auto",
												minHeight: "42px",
												"&:hover": {
													bgcolor: abaAtiva === "processos" ? "#138a7d" : "rgba(23, 162, 184, 0.08)",
												},
											}}
										>
											Processos
										</Button>
										<Button
											onClick={() => setAbaAtiva("plataforma")}
											sx={{
												textTransform: "none",
												fontSize: "15px",
												fontWeight: 500,
												bgcolor: abaAtiva === "plataforma" ? "#1a9e8f" : "transparent",
												color: abaAtiva === "plataforma" ? "white" : "#1a9e8f",
												borderRadius: "6px 6px 0 0",
												px: 3,
												py: 1.5,
												minWidth: "auto",
												minHeight: "42px",
												"&:hover": {
													bgcolor: abaAtiva === "plataforma" ? "#138a7d" : "rgba(23, 162, 184, 0.08)",
												},
											}}
										>
											Plataforma de Rastreio
										</Button>
									</>
								)}
							</Box>

							{/* Botão Adicionar documentos/produtos/processos */}
							{(abaAtiva === "empresa" || abaAtiva === "produtos" || abaAtiva === "processos") && (
								<Button
									variant="contained"
									onClick={
										abaAtiva === "empresa"
											? handleAddDocumentos
											: abaAtiva === "produtos"
											? handleAddProdutos
											: handleAddProcessos
									}
									sx={{
										bgcolor: "#1a9e8f",
										"&:hover": {
											bgcolor: "#138a7d",
											boxShadow: "0 2px 4px rgba(23, 162, 184, 0.3)",
										},
										textTransform: "none",
										px: 3,
										py: 1.25,
										fontSize: "15px",
										fontWeight: 500,
										borderRadius: "4px",
										boxShadow: "none",
									}}
								>
									{abaAtiva === "empresa"
										? "Adicionar documentos"
										: abaAtiva === "produtos"
										? "Adicionar produto"
										: "Adicionar processo"}
								</Button>
							)}
						</Box>

						{/* Conteúdo da aba */}
						<Box sx={{ px: 6, py: 4, minHeight: "400px" }}>{renderAbaContent()}</Box>

						{/* Botão Cadastrar no canto inferior direito */}
						<Box
							sx={{
								display: "flex",
								justifyContent: "flex-end",
								px: 6,
								pb: 4,
								pt: 0,
							}}
						>
							<Button
								variant="contained"
								onClick={!id && !naUltimaAba ? handleProximaAba : handleSalvar}
								disabled={salvando}
								sx={{
									bgcolor: "#1a9e8f",
									"&:hover": {
										bgcolor: "#138a7d",
										boxShadow: "0 2px 6px rgba(23, 162, 184, 0.4)",
									},
									"&:disabled": {
										bgcolor: "#ccc",
									},
									textTransform: "none",
									px: 5,
									py: 1.5,
									fontSize: "15px",
									fontWeight: 500,
									borderRadius: "4px",
									boxShadow: "none",
								}}
							>
								{salvando ? "Salvando..." : id ? "Atualizar" : naUltimaAba ? "Cadastrar" : "Próximo"}
							</Button>
						</Box>
					</Box>
			</Box>
		</AppLayout>
	);
}

export default DistribuidoraCad;
